import { cloneDeep } from 'lodash';
import { useCallback } from 'react';

/**
 * Wraps a parent "array of { key, payload }" settings updater to produce an updater for one of
 * the parent's object-valued child keys. The returned callback merges its changes into the
 * child's current value and writes the result back through the parent updater.
 *
 * This backs the nested settings hooks (e.g. `managementLists.costCodes`,
 * `dailyProjectReport.dailyReportPreviewSettings`) so they don't each re-implement the
 * clone/merge/write-through-parent dance. It composes to any depth: the parent updater can
 * itself be another nested updater.
 *
 * @param parentUpdate updater for the parent settings object (e.g. `useFeatureSettingsUpdate('managementLists')`)
 * @param currentChild the child's current value (e.g. `memberSettings?.web?.features?.managementLists?.costCodes`)
 * @param childKey the parent key the merged child should be written to (e.g. `'costCodes'`)
 */
export default function useNestedSettingsUpdate<ChildKey extends PropertyKey, ChildValue>(
  parentUpdate: (keysAndPayloads: { key: ChildKey; payload: ChildValue }[]) => unknown,
  currentChild: ChildValue | undefined,
  childKey: ChildKey
) {
  return useCallback(
    <K extends keyof NonNullable<ChildValue>>(
      keysAndPayloads: { key: K; payload: NonNullable<ChildValue>[K] }[]
    ) => {
      const cloned = cloneDeep(currentChild);

      // No changes: persist the existing child settings unchanged (matches prior behavior).
      if (keysAndPayloads.length === 0) {
        return parentUpdate([{ key: childKey, payload: cloned as ChildValue }]);
      }

      const mapped: Record<PropertyKey, unknown> = { ...cloned };
      keysAndPayloads.forEach(({ key, payload }) => {
        mapped[key] = payload;
      });

      return parentUpdate([{ key: childKey, payload: mapped as ChildValue }]);
    },
    [parentUpdate, currentChild, childKey]
  );
}
